import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';
import { formatDateLocaleLima } from '../../../utils/dateUtils';

const ComplianceAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [filter, setFilter] = useState('pending'); // pending | all

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await machineryService.getComplianceAlerts();
      if (response.success && Array.isArray(response.data)) {
        setAlerts(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar alertas', type: 'error' });
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar alertas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const visibleAlerts = filter === 'pending'
    ? alerts.filter((item) => item.status === 'pronto' || item.status === 'alerta')
    : alerts;

  const statusLabel = (status) => {
    if (status === 'alerta') return 'Vencido';
    if (status === 'pronto') return 'Por vencer (≤ 30 días)';
    return 'Al día';
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">SOAT y revisión técnica</h1>
            <p className="text-gray-600 mt-1">Vencimientos de documentación legal de vehículos</p>
          </div>
          <Link to="/machinery/vehicles" className="text-sm font-medium text-teal-600 hover:text-teal-700">
            ← Volver a vehículos
          </Link>
        </div>

        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'pending' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Pendientes
          </button>
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${filter === 'all' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Todos
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            <p className="mt-2 text-gray-600">Cargando alertas...</p>
          </div>
        ) : visibleAlerts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-600">
            {filter === 'pending' ? 'No hay documentos por vencer o vencidos.' : 'No hay vehículos con fechas de SOAT o revisión técnica registradas.'}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días restantes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleAlerts.map((item, idx) => (
                  <tr key={`${item.id}-${item.document_type}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{item.code} — {item.name}</div>
                      {item.license_plate && <div className="text-xs text-gray-500">Placa: {item.license_plate}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.document_label}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDateLocaleLima(item.expires_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.days_remaining < 0 ? `Vencido hace ${Math.abs(item.days_remaining)} día(s)` : `${item.days_remaining} día(s)`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        item.status === 'alerta' ? 'bg-red-100 text-red-800' : item.status === 'pronto' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/machinery/vehicles/${item.id}/edit`} className="text-sm font-medium text-teal-600 hover:text-teal-700">
                        Renovar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default ComplianceAlerts;
